import React, { useEffect, useMemo, useRef, useState } from 'react';
import { search } from './tree.js';

/** Full-screen search in edl.dk's display type: big field, big results. */
export function Search({ index, onClose, onSelect }) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const results = useMemo(() => search(index, query), [index, query]);

  useEffect(() => {
    inputRef.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
  useEffect(() => setActive(0), [query]);
  useEffect(() => {
    listRef.current?.querySelector('.is-active')?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const onKey = (e) => {
    if (e.key === 'Escape') onClose();
    else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(results.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter' && results[active]) onSelect(results[active].id);
  };

  return (
    <div className="search" role="dialog" aria-modal="true" aria-label="Search" onKeyDown={onKey}>
      <div className="search__inner">
        <div className="search__top">
          <label className="edl-label search__label" htmlFor="edl-search">
            Search
          </label>
          <button type="button" className="edl-link-button" onClick={onClose}>
            Close <kbd className="search__esc">Esc</kbd>
          </button>
        </div>
        <div className="edl-underline">
          <input
            ref={inputRef}
            id="edl-search"
            className="search__input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Component or story"
            autoComplete="off"
            spellCheck={false}
            role="combobox"
            aria-expanded={results.length > 0}
            aria-controls="edl-search-results"
            aria-activedescendant={results[active] ? `sr-${results[active].id}` : undefined}
          />
        </div>
        <ul className="search__results" id="edl-search-results" role="listbox" ref={listRef}>
          {results.map((e, i) => {
            const segs = e.title.split('/').map((s) => s.trim());
            return (
              <li key={e.id} id={`sr-${e.id}`} role="option" aria-selected={i === active}>
                <button type="button" className={`search__result${i === active ? ' is-active' : ''}`} onMouseEnter={() => setActive(i)} onClick={() => onSelect(e.id)}>
                  <span className="search__result-name">{e.type === 'docs' ? segs.at(-1) : `${segs.at(-1)} · ${e.name}`}</span>
                  <span className="edl-label search__result-path">{e.type === 'docs' ? (segs.length > 1 ? segs.slice(0, -1).join(' / ') : 'Page') : 'Story'}</span>
                </button>
              </li>
            );
          })}
        </ul>
        {query && results.length === 0 && <p className="search__none">No component or story matches “{query}”.</p>}
      </div>
    </div>
  );
}
